import uniq from 'lodash/uniq';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useEvent } from '@legacy-providers/event';

import { FormField } from '@shared/forms/components/FormField';
import { getRegistrationOptionsForEvent } from '@shared/registrations/endpoints/getRegistrationOptionsForEvent';
import { getRegistrations } from '@shared/registrations/endpoints/getRegistrations';
import { Button } from '@shared/ui/components/Button';
import { Select } from '@shared/ui/components/Select';

type RegistrationData = App.Domain.Registration.Data.RegistrationData;

export default function RegistrationMultiSelectField({ name, label }: { name: string; label: string }) {
  const event = useEvent();
  const form = useFormContext();
  const [registrations, setRegistrations] = useState<RegistrationData[]>([]);
  const [selectedRegistration, setSelectedRegistration] = useState<string | number | null>(null);

  const registrationIds: number[] = useWatch({ name });

  useEffect(() => {
    getRegistrations({ filter: { id: registrationIds?.join(',') }, page: { size: 100 } }).then((registrations) => {
      setRegistrations(registrations.data);
    });
  }, [registrationIds]);

  useEffect(() => {
    if (selectedRegistration === null) {
      return;
    }

    form.setValue(name, uniq([...form.getValues(name), selectedRegistration]));
    setSelectedRegistration(null);
  }, [selectedRegistration]);

  return (
    <FormField name={name} label={label}>
      {({ id, name, error, required, value, onChange }) => (
        <>
          <div className="flex flex-row gap-2">
            <Select
              className="min-w-[32ch]"
              options={(query) => getRegistrationOptionsForEvent(event.id, query)}
              onChange={setSelectedRegistration}
              value={selectedRegistration}
              placeholder="Select a registration"
            />
          </div>
          <ul role="list" className="divide-y divide-gray-100 max-w-[40ch]">
            {value?.map((registrationId: number) => (
              <li className="flex items-center justify-between gap-x-6 py-2">
                <div className="min-w-0 gap-x-4">
                  <p className="leading-6 text-gray-900">
                    {registrations.find((registration) => registration.id === registrationId)?.company.name}
                  </p>
                </div>
                <Button
                  onClick={() => onChange(value.filter((id: number) => id !== registrationId))}
                  variant="outline-destructive"
                  size="xs"
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </FormField>
  );
}
